import React from 'react';
import { cn } from '@/lib/utils';
import { ColorVariant } from '@/components/ui/SidebarFilterButtons';

interface ColorPickerProps {
  selectedColor: ColorVariant;
  onColorChange: (color: ColorVariant) => void;
  className?: string;
}

const colorConfig: Record<ColorVariant, { bg: string; ring: string; text: string; name: string }> = {
  purple: { bg: 'bg-purple-500', ring: 'ring-purple-500', text: 'text-purple-500', name: 'Purple' },
  green: { bg: 'bg-green-500', ring: 'ring-green-500', text: 'text-green-500', name: 'Green' },
  amber: { bg: 'bg-amber-500', ring: 'ring-amber-500', text: 'text-amber-500', name: 'Amber' },
  blue: { bg: 'bg-blue-500', ring: 'ring-blue-500', text: 'text-blue-500', name: 'Blue' },
  red: { bg: 'bg-red-500', ring: 'ring-red-500', text: 'text-red-500', name: 'Red' },
  indigo: { bg: 'bg-indigo-500', ring: 'ring-indigo-500', text: 'text-indigo-500', name: 'Indigo' },
  gray: { bg: 'bg-gray-500', ring: 'ring-gray-500', text: 'text-gray-500', name: 'Gray' },
  orange: { bg: 'bg-orange-500', ring: 'ring-orange-500', text: 'text-orange-500', name: 'Orange' },
  brown: { bg: 'bg-amber-800', ring: 'ring-amber-800', text: 'text-amber-800', name: 'Brown' },
  white: { bg: 'bg-white border border-gray-300', ring: 'ring-gray-400', text: 'text-gray-600', name: 'White' },
  black: { bg: 'bg-black', ring: 'ring-gray-600', text: 'text-black', name: 'Black' },
};

// Helper function to get text color class for icons
export const getColorClass = (color: ColorVariant): string => {
  return colorConfig[color]?.text || 'text-purple-500';
};

const ColorPicker: React.FC<ColorPickerProps> = ({ selectedColor, onColorChange, className }) => {
  return (
    <div className={cn("flex flex-wrap gap-2 p-3 bg-background/95 backdrop-blur-sm border border-accent/20 rounded-lg shadow-lg", className)}>
      {(Object.keys(colorConfig) as ColorVariant[]).map((color) => {
        const config = colorConfig[color];
        const isSelected = selectedColor === color;
        
        return (
          <button
            key={color}
            onClick={() => onColorChange(color)}
            className={cn(
              "w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 focus:outline-none focus:scale-110",
              config.bg,
              isSelected && `ring-2 ring-offset-2 ${config.ring} scale-110`
            )}
            title={config.name}
            aria-label={`Select ${config.name} color`}
          />
        );
      })}
    </div>
  );
};

export default ColorPicker; 